import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp } from "./Reveal";

const IMG_BERRY =
  "https://images.unsplash.com/photo-1761991766193-6295578152bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHx2aWJyYW50JTIwZnJlc2glMjBmcnVpdHMlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODY2NzAxNjd8MA&ixlib=rb-4.1.0&q=85&w=900";

const PLAY_URL = "https://play.google.com/store/apps/details?id=com.myfreshtracker.app&pli=1";
const APPSTORE_URL = "https://apps.apple.com/ca/app/my-fresh-tracker/id6814677216";

// Swap these with real figures once the store console reports them.
const STORE_STATS = [
  {
    value: "Live",
    label: "on Google Play — download numbers will show here as they grow",
    href: PLAY_URL,
    testid: "stats-play-tile",
  },
  {
    value: "New",
    label: "on the App Store — ratings will show here as they grow",
    href: APPSTORE_URL,
    testid: "stats-appstore-tile",
  },
];

const STATS = [
  { value: "40%", label: "of food produced worldwide is never eaten" },
  { value: "$1,500", label: "thrown away per household, every year" },
  { value: "3 taps", label: "to log a full grocery run in My Fresh Tracker" },
];

export default function Stats() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section className="px-6 md:px-12 py-24 md:py-40" data-testid="stats-section">
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <FadeUp>
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-[#16783F]">
              The problem — waste, by the numbers
            </p>
            <h2 className="font-serif font-light tracking-tight text-4xl md:text-6xl mt-6 max-w-xl">
              The average bin eats <em className="text-[#1F9D55]">better</em> than it should.
            </h2>
          </FadeUp>
          <div className="mt-16 border-t border-[#111111]/10">
            {STATS.map((s, i) => (
              <FadeUp key={s.value} delay={i * 0.12}>
                <div className="flex items-baseline gap-6 md:gap-10 py-8 border-b border-[#111111]/10">
                  <span className="font-serif font-light text-5xl md:text-7xl tracking-tight text-[#0A130B] w-40 md:w-56 shrink-0">
                    {s.value}
                  </span>
                  <span className="text-sm md:text-base text-[#111111]/60 max-w-xs">{s.label}</span>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.35}>
            <div
              className="mt-10 grid sm:grid-cols-2 gap-px bg-[#111111]/10 border border-[#111111]/10"
              data-testid="stats-store-strip"
            >
              {STORE_STATS.map((s) => (
                <a
                  key={s.testid}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={s.testid}
                  className="bg-[#F7F6F2] p-6 md:p-8 group"
                >
                  <p className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#0A130B] group-hover:text-[#16783F] transition-colors duration-300">
                    {s.value}
                  </p>
                  <p className="mt-2 text-sm text-[#111111]/60">{s.label}</p>
                </a>
              ))}
            </div>
          </FadeUp>
        </div>
        <div className="md:col-span-4 md:col-start-9 relative">
          <FadeUp delay={0.15}>
            <div ref={imgRef} className="rounded-[999px] overflow-hidden border border-[#111111]/10 h-[420px] md:h-[540px]">
              <motion.img
                src={IMG_BERRY}
                alt="A single raspberry, spot-lit against a black background"
                style={{ y }}
                className="w-full h-full object-cover scale-110"
                data-testid="stats-image"
              />
            </div>
            <div
              className="absolute -top-5 -right-3 rotate-[10deg] bg-[#F2C94C] text-[#0A130B] px-5 py-2 rounded-full border border-[#111111]/15"
              data-testid="stats-badge"
            >
              <span className="font-serif italic text-base md:text-lg">est. 2026</span>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
